import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { deleteSessionApiSessionsSessionIdDelete } from "../../generated/api/sdk.gen";
import type {
  ProjectSummary,
  SlimSessionRef,
} from "../../generated/api/types.gen";
import { invalidateGallerySessionCaches } from "../../lib/queries";
import { Button } from "../Button";
import { ConfirmationModal } from "../ConfirmationModal";
import { GallerySessionDetailView } from "./GallerySessionDetailView";
import { buildProjectAncestorChain, getDesignTitle } from "./galleryTree";

interface GallerySessionDetailPanelProps {
  sessionId: string;
  allProjects: ProjectSummary[];
  allSessions: SlimSessionRef[];
  onExitSessionView: () => void;
  onGalleryNavigateRoot: () => void;
  onGalleryNavigateToProject: (projectId: string) => void;
}

interface SessionBreadcrumbsProps {
  breadcrumbChain: { id: string; name: string }[];
  sessionCrumbTitle: string;
  onGalleryNavigateRoot: () => void;
  onGalleryNavigateToProject: (projectId: string) => void;
}

function useSessionDelete(sessionId: string, onExitSessionView: () => void) {
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteSessionApiSessionsSessionIdDelete({
        path: { session_id: id },
      });
    },
    onSuccess: async () => {
      // Deleting exits back to the grid without remounting the gallery, so
      // both gallery session caches need the same invalidation or the deleted
      // session lingers until a hard refresh.
      await invalidateGallerySessionCaches(queryClient);
      setShowDeleteModal(false);
      onExitSessionView();
    },
  });

  return {
    showDeleteModal,
    setShowDeleteModal,
    deleteSession: () => deleteMutation.mutate(sessionId),
  };
}

function SessionBreadcrumbs({
  breadcrumbChain,
  sessionCrumbTitle,
  onGalleryNavigateRoot,
  onGalleryNavigateToProject,
}: SessionBreadcrumbsProps) {
  return (
    <nav
      className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[14px] leading-6 text-[var(--text-secondary)]"
      aria-label="Gallery location"
    >
      <button
        type="button"
        onClick={onGalleryNavigateRoot}
        className="shrink-0 rounded text-left transition hover:text-[var(--text-primary)]"
      >
        My Projects
      </button>
      {breadcrumbChain.map((segment) => (
        <span
          key={segment.id}
          className="inline-flex min-w-0 items-center gap-x-2"
        >
          <span className="text-[var(--text-secondary)]/70" aria-hidden>
            &gt;
          </span>
          <button
            type="button"
            onClick={() => onGalleryNavigateToProject(segment.id)}
            className="min-w-0 max-w-[220px] truncate rounded text-left transition hover:text-[var(--text-primary)] md:max-w-[320px]"
          >
            {segment.name}
          </button>
        </span>
      ))}
      <span className="text-[var(--text-secondary)]/70" aria-hidden>
        &gt;
      </span>
      <span className="min-w-0 font-medium text-[var(--text-primary)]">
        {sessionCrumbTitle}
      </span>
    </nav>
  );
}

export function GallerySessionDetailPanel({
  sessionId,
  allProjects,
  allSessions,
  onExitSessionView,
  onGalleryNavigateRoot,
  onGalleryNavigateToProject,
}: GallerySessionDetailPanelProps) {
  const { showDeleteModal, setShowDeleteModal, deleteSession } =
    useSessionDelete(sessionId, onExitSessionView);

  const sessionForBreadcrumb = useMemo(
    () => allSessions.find((s) => s.session_id === sessionId),
    [allSessions, sessionId],
  );
  const breadcrumbChain = useMemo(
    () =>
      buildProjectAncestorChain(
        sessionForBreadcrumb?.project_id ?? undefined,
        allProjects,
      ),
    [allProjects, sessionForBreadcrumb?.project_id],
  );
  const sessionCrumbTitle = sessionForBreadcrumb
    ? getDesignTitle(sessionForBreadcrumb)
    : "Design";

  return (
    <div className="flex min-w-0 flex-1 flex-col bg-[#20272e]">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-3 px-7 py-4">
        <SessionBreadcrumbs
          breadcrumbChain={breadcrumbChain}
          sessionCrumbTitle={sessionCrumbTitle}
          onGalleryNavigateRoot={onGalleryNavigateRoot}
          onGalleryNavigateToProject={onGalleryNavigateToProject}
        />
        <Button
          variant="destructive"
          className="shrink-0"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete Project
        </Button>
      </div>
      <div className="scrollbar-hide flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <GallerySessionDetailView
          sessionId={sessionId}
          onExit={onExitSessionView}
        />
      </div>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteSession}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone and will remove all images in this session."
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
}
